import type {
  ActionGroupCandidate,
  BranchesActionGroupCandidate,
} from "../../types/candidate";
import {
  collectActionsDeep,
  findGroupByInstanceId,
} from "../../stores/automation/utils/action-group-walk";
import type { CurrSection } from "./types";

const ACTION_PARAM = "action";
const BRANCH_PARAM = "branch";
const ELSE_BRANCH = "else";
const WHOLE_NUMBER = /^\d+$/;

/** `action` is the instance id of an action, loop or Conditional Logic block
 * (the Copilot tools call it `stepId`); `branch` is a 1-based position in its
 * Conditional Logic block, or "else". */
export interface SelectionParams {
  action?: string;
  branch?: string;
}

// Same rule as LoopingStore: the root loop is the first top-level loop.
const findRootLoop = (
  groups: ActionGroupCandidate[],
): ActionGroupCandidate | undefined =>
  groups.find((group) => group.config.oneofKind === "loop");

export const selectionParamsFor = (
  section: CurrSection,
  groups: ActionGroupCandidate[],
): SelectionParams => {
  if (section == null) return {};
  switch (section.type) {
    case "action": {
      const instanceId = collectActionsDeep(groups).find(
        (action) => action.hash === section.hash,
      )?.actionReference?.instanceId;
      return instanceId === undefined ? {} : { action: String(instanceId) };
    }
    case "looping": {
      const loop = findRootLoop(groups);
      return loop === undefined ? {} : { action: String(loop.instanceId) };
    }
    case "nested-loop":
    case "branches":
      return findGroupByInstanceId(groups, section.instanceId) === undefined
        ? {}
        : { action: String(section.instanceId) };
    case "branch": {
      const block = findGroupByInstanceId(groups, section.instanceId);
      if (block?.config.oneofKind !== "branches") return {};
      const action = String(section.instanceId);
      const { branches, elseBranch } = block.config.branches;
      // The else branch is selected by this sentinel, not by its hash
      // (BranchesStore.findBranch).
      if (section.branchHash === ELSE_BRANCH) {
        return elseBranch === undefined
          ? { action }
          : { action, branch: ELSE_BRANCH };
      }
      const index = branches.findIndex(
        (branch) => branch.hash === section.branchHash,
      );
      return index === -1 ? { action } : { action, branch: String(index + 1) };
    }
    default:
      return {};
  }
};

export const readSelectionParams = (
  search: URLSearchParams,
): SelectionParams => ({
  action: search.get(ACTION_PARAM) ?? undefined,
  branch: search.get(BRANCH_PARAM) ?? undefined,
});

const branchSection = (
  instanceId: number,
  block: BranchesActionGroupCandidate,
  branch: string | undefined,
): NonNullable<CurrSection> => {
  if (branch === ELSE_BRANCH && block.elseBranch !== undefined) {
    return { type: "branch", instanceId, branchHash: ELSE_BRANCH };
  }
  const target =
    branch !== undefined && WHOLE_NUMBER.test(branch)
      ? block.branches[Number(branch) - 1]
      : undefined;
  return target === undefined
    ? { type: "branches", instanceId }
    : { type: "branch", instanceId, branchHash: target.hash };
};

/** undefined: the URL names no action, or one that is not there. */
export const resolveSelection = (
  params: SelectionParams,
  groups: ActionGroupCandidate[],
): NonNullable<CurrSection> | undefined => {
  if (params.action === undefined || !WHOLE_NUMBER.test(params.action)) {
    return undefined;
  }
  const instanceId = Number(params.action);

  const action = collectActionsDeep(groups).find(
    (candidate) => candidate.actionReference?.instanceId === instanceId,
  );
  if (action !== undefined) return { type: "action", hash: action.hash };

  const group = findGroupByInstanceId(groups, instanceId);
  if (group?.config.oneofKind === "loop") {
    return findRootLoop(groups) === group
      ? { type: "looping", hash: "" }
      : { type: "nested-loop", instanceId };
  }
  if (group?.config.oneofKind === "branches") {
    return branchSection(instanceId, group.config.branches, params.branch);
  }
  return undefined;
};

export const withSelectionParams = (
  search: URLSearchParams,
  params: SelectionParams,
): URLSearchParams => {
  const next = new URLSearchParams(search);
  next.delete(ACTION_PARAM);
  next.delete(BRANCH_PARAM);
  if (params.action !== undefined) next.set(ACTION_PARAM, params.action);
  if (params.branch !== undefined) next.set(BRANCH_PARAM, params.branch);
  return next;
};
